import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileCheck2, Workflow, ShieldCheck, Cpu, Check } from "lucide-react";
import { SERVICES } from "../../lib/site-content";
import { Reveal } from "./Reveal";

const ICONS = { FileCheck2, Workflow, ShieldCheck, Cpu };

export const Services = () => {
  const [active, setActive] = useState(SERVICES[0].id);
  const current = SERVICES.find((s) => s.id === active);
  const ActiveIcon = ICONS[current.icon];

  return (
    <section
      id="diensten"
      data-testid="services-section"
      className="relative overflow-hidden bg-[#0F172A] py-24 text-white grain lg:py-32"
    >
      <div className="pointer-events-none absolute -right-32 top-20 h-96 w-96 rounded-full bg-[#F59E0B]/10 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-[#F59E0B]">
            Onze Diensten
          </p>
          <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Van vergunning tot dagelijkse opvolging
          </h2>
          <p className="mt-5 text-lg text-slate-300">
            Vier kerndiensten die samen uw volledige accijnsflow afdekken —
            preventief, digitaal en operationeel.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12">
          {/* Tabs */}
          <div className="flex flex-col gap-2 lg:col-span-5">
            {SERVICES.map((s) => {
              const Icon = ICONS[s.icon];
              const on = s.id === active;
              return (
                <button
                  key={s.id}
                  data-testid={`service-tab-${s.id}`}
                  onClick={() => setActive(s.id)}
                  className={`group relative flex items-center gap-4 rounded-2xl border px-5 py-4 text-left transition-colors duration-300 ${
                    on
                      ? "border-[#F59E0B]/40 bg-white/[0.06]"
                      : "border-white/10 bg-transparent hover:bg-white/[0.03]"
                  }`}
                >
                  <span
                    className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl transition-colors duration-300 ${
                      on
                        ? "bg-[#F59E0B] text-[#0F172A]"
                        : "bg-white/10 text-white"
                    }`}
                  >
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <span className="font-display text-lg font-semibold">
                    {s.tab}
                  </span>
                  {on && (
                    <motion.span
                      layoutId="service-active-bar"
                      className="absolute inset-y-3 left-0 w-1 rounded-full bg-[#F59E0B]"
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Panel */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id}
                data-testid={`service-panel-${current.id}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="h-full rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-sm lg:p-10"
              >
                <span className="grid h-14 w-14 place-items-center rounded-2xl bg-[#F59E0B] text-[#0F172A]">
                  <ActiveIcon className="h-7 w-7" strokeWidth={2} />
                </span>
                <h3 className="mt-6 font-display text-2xl font-bold text-white lg:text-3xl">
                  {current.title}
                </h3>
                <p className="mt-4 text-lg leading-relaxed text-slate-300">
                  {current.desc}
                </p>
                <ul className="mt-8 space-y-3">
                  {current.points.map((p) => (
                    <li key={p} className="flex items-start gap-3">
                      <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#F59E0B]/15 text-[#F59E0B]">
                        <Check className="h-3.5 w-3.5" strokeWidth={3} />
                      </span>
                      <span className="text-slate-200">{p}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
